import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { t } from "@/i18n";
import { eur, pct, severityOf } from "@/lib/format";

const SEV_COLOR = { green: "#10B981", orange: "#F59E0B", red: "#F43F5E" };
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11, tickLine: false, axisLine: false };

function Tip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-popover/90 px-3 py-2 text-xs shadow-lg backdrop-blur-md">
      <p className="mb-1 font-semibold">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.payload.fill }} />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="num ml-auto font-semibold">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

export function MarginEvolutionChart({ data }) {
  return (
    <div className="h-[300px] flex-1 lg:h-auto lg:min-h-[300px]">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ left: -10, right: 8, top: 8 }}>
        <defs>
          <linearGradient id="gMargin" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gFc" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366F1" stopOpacity={0.25} />
            <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="month" {...axis} />
        <YAxis {...axis} tickFormatter={(v) => `${v}%`} />
        <Tooltip content={<Tip fmt={(v) => pct(v)} />} />
        <Area type="monotone" dataKey="margin_pct" name="Marge brute" stroke="#10B981" strokeWidth={2.2} fill="url(#gMargin)" />
        <Area type="monotone" dataKey="food_cost_pct" name={t("common.food_cost")} stroke="#6366F1" strokeWidth={2.2} fill="url(#gFc)" />
      </AreaChart>
    </ResponsiveContainer>
    </div>
  );
}

export function InflationChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 40)}>
      <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={120} {...axis} tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
        <Tooltip cursor={{ fill: "hsl(var(--muted))" }} content={<Tip fmt={(v) => pct(v, true)} />} />
        <Bar dataKey="change_pct" name="Hausse" radius={[0, 6, 6, 0]} maxBarSize={22}>
          {data.map((d) => <Cell key={d.id} fill={SEV_COLOR[severityOf(d.change_pct)]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DishesChart({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 44)}>
      <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={130} {...axis} tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
        <Tooltip cursor={{ fill: "hsl(var(--muted))" }} content={<Tip fmt={(v) => eur(v)} />} />
        <Bar dataKey="gross_margin" name={t("charts.margin_per_dish")} fill={color} radius={[0, 6, 6, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
}
